/**
 * Per-browser active dataset selection.
 *
 * The selected OCEL file is a per-browser choice stored in localStorage —
 * NOT the server-global active file, which only serves as a default for
 * first-time visitors. Every API call must carry file_type explicitly.
 *
 * For logged-in users, setStoredActiveFile also best-effort syncs the
 * selection to their Supabase user_dashboard_state row (see
 * services/userDashboardState.js), so it follows them across browsers/
 * devices. This is the single choke point for that sync — every call site
 * (Open Project, File Manager switch, delete-fallback, mount-time
 * resolution) gets it for free.
 */

import { supabase } from '../lib/supabase';
import { upsertUserDashboardState } from './userDashboardState';
import { touchActiveFileConfig } from './api/dashboardConfig.api';

const KEY = 'cintelion.activeFile';

export const getStoredActiveFile = () => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};

export const setStoredActiveFile = (fileType) => {
  try {
    if (fileType) {
      localStorage.setItem(KEY, fileType);
    } else {
      localStorage.removeItem(KEY);
    }
  } catch {
    // localStorage unavailable (private mode quota etc.) — selection just
    // won't survive a reload; the server default fallback still applies.
  }

  // Best-effort cross-device sync for logged-in users. Fire-and-forget: this
  // function's contract stays synchronous for localStorage, and a failed/slow
  // sync never blocks or breaks the current tab — it's a convenience for
  // *other* devices, not this session's source of truth.
  if (fileType) {
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!session?.user) return;
        // last_active_file_set_at is what "always use latest scheduled data"
        // compares against a scheduled activation's activated_at — only a run
        // NEWER than the user's own selection may auto-jump them, so a manual
        // switch back to an older dataset survives reloads until the next run.
        return upsertUserDashboardState({
          last_active_file_type: fileType,
          last_active_file_set_at: new Date().toISOString(),
        });
      })
      .catch(() => {});

    // Keep the saved dashboard config's active_file provenance fresh too — it's
    // the only tenant signal cache_warming's _config_matches_tenant uses to decide
    // whether this user's config gets replayed against a warm pass. Independent of
    // login state (works for the "default" identity when auth is disabled too),
    // so this call is not gated behind the Supabase session check above.
    touchActiveFileConfig(fileType).catch(() => {});
  }
};
